import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { WellbeingData } from '../types/wellbeing';
import { getWellbeingEntries, deleteWellbeingEntry } from '../lib/wellbeingService';
import { exportService } from '../lib/exportService';

type WellbeingEntry = WellbeingData & { id: string; createdAt: string };

export const Wellbeing = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<WellbeingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalDate, setModalDate] = useState<string | null>(null);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await getWellbeingEntries();
      setEntries(data);
    } catch (error) {
      console.error('Error loading wellbeing entries:', error);
      toast.error('Nie udało się załadować wpisów');
    } finally {
      setLoading(false);
    }
  };

  const entriesByDate = useMemo(() => {
    const map: Record<string, WellbeingEntry[]> = {};
    for (const e of entries) {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    }
    for (const arr of Object.values(map)) {
      arr.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    return map;
  }, [entries]);

  const sortedDates = useMemo(() => Object.keys(entriesByDate).sort((a, b) => b.localeCompare(a)), [entriesByDate]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten wpis?')) return;

    try {
      await deleteWellbeingEntry(id);
      toast.success('Wpis usunięty');
      loadEntries();
      const date = entries.find(e => e.id === id)?.date;
      if (date && entriesByDate[date]?.length <= 1) setModalDate(null);
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Nie udało się usunąć');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getFilledFieldsCount = (entry: WellbeingData) => {
    const allFields = Object.values(entry).filter(v =>
      v !== undefined && v !== null && v !== '' && v !== 0 && v !== false
    );
    return allFields.length;
  };

  const filterEntriesByPeriod = (period: 'week' | 'biweek' | 'month') => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let from: Date;

    switch (period) {
      case 'week':
        from = new Date(today);
        from.setDate(from.getDate() - 7);
        break;
      case 'biweek':
        from = new Date(today);
        from.setDate(from.getDate() - 14);
        break;
      case 'month':
        from = new Date(today);
        from.setMonth(from.getMonth() - 1);
        break;
      default:
        from = new Date(0);
    }

    return entries.filter(e => {
      const d = new Date(e.date);
      d.setHours(0, 0, 0, 0);
      return d >= from && d <= today;
    });
  };

  const handleDownloadReport = (period: 'week' | 'biweek' | 'month') => {
    const filtered = filterEntriesByPeriod(period);
    const labels = { week: 'tygodniowy', biweek: 'dwutygodniowy', month: 'miesięczny' };
    const periodLabel = labels[period];

    if (filtered.length === 0) {
      toast.error(`Brak wpisów do raportu ${periodLabel}`);
      return;
    }

    const now = new Date();
    let filename = `raport_samopoczucie_${periodLabel}`;
    if (period === 'week') {
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - 7);
      filename = `raport_samopoczucie_tygodniowy_${weekStart.toISOString().split('T')[0]}_${now.toISOString().split('T')[0]}`;
    } else if (period === 'biweek') {
      const biStart = new Date(now);
      biStart.setDate(biStart.getDate() - 14);
      filename = `raport_samopoczucie_dwutygodniowy_${biStart.toISOString().split('T')[0]}_${now.toISOString().split('T')[0]}`;
    } else {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      filename = `raport_samopoczucie_miesieczny_${monthStart.toISOString().split('T')[0]}_${now.toISOString().split('T')[0]}`;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    exportService.exportWellbeingHistoryToPDF(filtered as any, filename, periodLabel);
    toast.success(`Raport ${periodLabel} pobrany`);
  };

  const modalEntries = modalDate ? entriesByDate[modalDate] || [] : [];

  const ReportButton = ({ period, label }: { period: 'week' | 'biweek' | 'month'; label: string }) => (
    <button
      onClick={() => handleDownloadReport(period)}
      style={{
        padding: '8px 16px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        background: 'white',
        color: '#475569',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#f8fafc';
        e.currentTarget.style.borderColor = '#6B7FFF';
        e.currentTarget.style.color = '#6B7FFF';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'white';
        e.currentTarget.style.borderColor = '#e2e8f0';
        e.currentTarget.style.color = '#475569';
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/>
        <line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
      {label}
    </button>
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ color: '#64748b' }}>Ładowanie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/wellbeing')}
            style={{
              padding: '6px',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              color: '#6B7FFF',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f4ff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>

          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
            Historia wpisów
          </h1>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {entries.length > 0 && (
            <>
              <ReportButton period="week" label="Raport tygodniowy" />
              <ReportButton period="biweek" label="Raport dwutygodniowy" />
              <ReportButton period="month" label="Raport miesięczny" />
            </>
          )}

          <button
            onClick={() => navigate('/wellbeing')}
            style={{
              padding: '10px 18px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(107, 127, 255, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Dodaj wpis
          </button>
        </div>
      </div>

      {/* Lista wpisów / Empty state */}
      {entries.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          background: 'white',
          borderRadius: '16px',
          border: '2px dashed #e2e8f0',
        }}>
          <div style={{
            width: 50,
            height: 50,
            margin: '0 auto 14px',
            borderRadius: '50%',
            background: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
              <line x1="9" y1="9" x2="9.01" y2="9"/>
              <line x1="15" y1="9" x2="15.01" y2="9"/>
            </svg>
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', margin: '0 0 6px' }}>Brak wpisów</h3>
          <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>Zacznij śledzić swoje samopoczucie</p>
          <button
            onClick={() => navigate('/wellbeing')}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: '2px solid #6B7FFF',
              background: 'white',
              color: '#6B7FFF',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Dodaj pierwszy wpis
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {sortedDates.map((date) => {
            const dayEntries = entriesByDate[date];
            const count = dayEntries.length;

            return (
              <div
                key={date}
                onClick={() => setModalDate(date)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setModalDate(date)}
                style={{
                  background: 'white',
                  borderRadius: 12,
                  padding: '14px 16px',
                  border: '2px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#6B7FFF';
                  e.currentTarget.style.background = '#fafbff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', margin: 0 }}>
                    {formatDateShort(date)}
                  </h3>
                  <span style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#6B7FFF',
                    padding: '4px 10px',
                    borderRadius: 6,
                    background: '#f0f4ff',
                  }}>
                    {count} {count === 1 ? 'wpis' : count < 5 ? 'wpisy' : 'wpisów'}
                  </span>
                </div>
                <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0' }}>
                  Kliknij, aby zobaczyć szczegóły
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal – wpisy danego dnia */}
      {modalDate && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => setModalDate(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 24,
              maxWidth: 560,
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                {formatDateShort(modalDate)}
              </h2>
              <button
                onClick={() => setModalDate(null)}
                style={{
                  padding: 6,
                  borderRadius: 8,
                  border: 'none',
                  background: '#f1f5f9',
                  color: '#64748b',
                  cursor: 'pointer',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {modalEntries.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    background: '#fafafa',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{formatDate(entry.createdAt)}</span>
                    <span style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#6B7FFF',
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: '#f0f4ff',
                    }}>
                      {getFilledFieldsCount(entry)} pól
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#475569', marginBottom: 12, lineHeight: 1.9 }}>
                    {/* Samopoczucie - pełna lista */}
                    <div style={{ marginBottom: 12 }}>
                      <strong style={{ color: '#64748b', fontSize: 12 }}>Samopoczucie</strong>
                      <div style={{ marginTop: 6 }}>
                        <div>Nastrój: {(entry.mood !== undefined && entry.mood > 0) ? `${entry.mood}/5` : '—'}</div>
                        <div>Poziom stresu: {(entry.stress !== undefined && entry.stress > 0) ? `${entry.stress}/5` : '—'}</div>
                        {entry.stressNote && <div style={{ paddingLeft: 12, fontStyle: 'italic' }}>Co zestresowało: {entry.stressNote}</div>}
                        <div>Energia/zmeżenie: {(entry.energy !== undefined && entry.energy > 0) ? `${entry.energy}/5` : '—'}</div>
                        <div>Ilość snu: {entry.sleepHours ? `${entry.sleepHours}h` : '—'}</div>
                        <div>Jakość snu: {(entry.sleepQuality !== undefined && entry.sleepQuality > 0) ? `${entry.sleepQuality}/5` : '—'}</div>
                      </div>
                    </div>
                    {/* Objawy jelitowe - pełna lista */}
                    <div style={{ marginBottom: 12 }}>
                      <strong style={{ color: '#64748b', fontSize: 12 }}>Objawy jelitowe</strong>
                      <div style={{ marginTop: 6 }}>
                        <div>Ból brzucha: {(entry.abdominalPain !== undefined && entry.abdominalPain > 0) ? `${entry.abdominalPain}/5` : '—'}</div>
                        <div>Wzdęcia/gazy: {(entry.bloating !== undefined && entry.bloating > 0) ? `${entry.bloating}/5` : '—'}</div>
                        <div>Przelewanie: {(entry.rumbling !== undefined && entry.rumbling > 0) ? `${entry.rumbling}/5` : '—'}</div>
                        <div>Zgaga: {(entry.heartburn !== undefined && entry.heartburn > 0) ? `${entry.heartburn}/5` : '—'}</div>
                        <div>Nudności: {(entry.nausea !== undefined && entry.nausea > 0) ? `${entry.nausea}/5` : '—'}</div>
                        <div>Wymioty: {(entry.vomiting !== undefined && entry.vomiting > 0) ? `${entry.vomiting}/5` : '—'}</div>
                        <div>Brak apetytu: {(entry.lossOfAppetite !== undefined && entry.lossOfAppetite > 0) ? `${entry.lossOfAppetite}/5` : '—'}</div>
                        <div>Uczucie pełności: {(entry.fullness !== undefined && entry.fullness > 0) ? `${entry.fullness}/5` : '—'}</div>
                      </div>
                    </div>
                    {/* Objawy pozajelitowe - pełna lista */}
                    <div style={{ marginBottom: 12 }}>
                      <strong style={{ color: '#64748b', fontSize: 12 }}>Objawy pozajelitowe</strong>
                      <div style={{ marginTop: 6 }}>
                        <div>Rumień guzowaty: {entry.erythemaNodosum ? 'Tak' : '—'}</div>
                        <div>Piodermia zgorzelinowa: {entry.pyodermaGangrenosum ? 'Tak' : '—'}</div>
                        <div>Inne (skóra): {entry.skinOther ? (entry.skinOtherNote || 'Tak') : '—'}</div>
                        <div>Problemy z oczami: {entry.eyeProblems ? 'Tak' : '—'}</div>
                        <div>Afty w ustach: {entry.mouthSores ? 'Tak' : '—'}</div>
                      </div>
                    </div>
                    {/* Jedzenie - pełna lista */}
                    <div style={{ marginBottom: 12 }}>
                      <strong style={{ color: '#64748b', fontSize: 12 }}>Jedzenie</strong>
                      <div style={{ marginTop: 6 }}>
                        <div>Zmiana względem jedzenia: {entry.dietChange ? (entry.dietChangeNote ? entry.dietChangeNote : 'Tak') : '—'}</div>
                        <div>Ciężkość posiłków: {(entry.mealHeaviness !== undefined && entry.mealHeaviness > 0) ? `${entry.mealHeaviness}/5` : '—'}</div>
                        <div>Piłeś alkohol: {entry.alcohol ? (entry.alcoholNote ? entry.alcoholNote : 'Tak') : '—'}</div>
                      </div>
                    </div>
                    {/* Inne - pełna lista */}
                    <div>
                      <strong style={{ color: '#64748b', fontSize: 12 }}>Inne</strong>
                      <div style={{ marginTop: 6 }}>
                        <div>Aktywność fizyczna: {(entry.physicalActivity !== undefined && entry.physicalActivity > 0) ? `${entry.physicalActivity}/5` : '—'}</div>
                        <div>Waga: {entry.weight ? entry.weight : '—'}</div>
                        <div>Gorączka: {entry.fever ? (entry.temperature ? `${entry.temperature}°C` : 'Tak') : '—'}</div>
                        <div>Notatki ogólne: {entry.notes || '—'}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => navigate(`/wellbeing?date=${entry.date}`)}
                      style={{
                        flex: 1,
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '2px solid #6B7FFF',
                        background: 'white',
                        color: '#6B7FFF',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Edytuj
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                      style={{
                        flex: 1,
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '2px solid #fee2e2',
                        background: '#fef2f2',
                        color: '#dc2626',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Usuń
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
